import { useCallback, useMemo, useRef, useState } from "react";
import axios from "axios";
import { baseUrl } from "../assets/Animations";
import { getRequestHeaders } from "../api";

const DEFAULT_BUCKET = "17:00-18:00"; // weekday 5pm bucket

const makeKey = (schoolId, bucket, fromKey, toKey) =>
  `${schoolId || "na"}|${bucket || DEFAULT_BUCKET}|${fromKey}=>${toKey}`;

export default function useDriveMatrix(schoolId, bucket = DEFAULT_BUCKET) {
  const [minutesMap, setMinutesMap] = useState(new Map());
  const pending = useRef(new Map());

  const ensureEdge = useCallback(async () => null, []);

  const minutesBetween = useCallback(
    (fromKey, toKey) => {
      if (!fromKey || !toKey || !schoolId) return null;
      const key = makeKey(schoolId, bucket, fromKey, toKey);
      return minutesMap.get(key);
    },
    [bucket, minutesMap, schoolId]
  );

  const ensureAllPairs = useCallback(
    async (input) => {
      if (!schoolId) return;
      let pairs = [];
      if (Array.isArray(input) && input.length > 0 && input[0]?.fromKey) {
        pairs = input.filter((p) => p.fromKey && p.toKey && p.fromKey !== p.toKey);
      } else if (Array.isArray(input)) {
        const uniqueKeys = Array.from(new Set(input.filter(Boolean)));
        if (uniqueKeys.length < 2) return;
        for (let i = 0; i < uniqueKeys.length; i++) {
          for (let j = 0; j < uniqueKeys.length; j++) {
            if (i === j) continue;
            const fromKey = uniqueKeys[i];
            const toKey = uniqueKeys[j];
            pairs.push({ fromKey, toKey });
          }
        }
      } else {
        return;
      }
      pairs = pairs.filter((p) => {
        const key = makeKey(schoolId, bucket, p.fromKey, p.toKey);
        return !minutesMap.has(key) && !pending.current.has(key);
      });
      if (!pairs.length) return;
      let resultEdges = [];
      const body = { schoolId, bucket, todBucket: bucket, pairs };
      const req = axios
        .post(`${baseUrl}/drive-edges/fill`, body, {
          headers: {
            "Content-Type": "application/json",
            ...(getRequestHeaders()?.headers || {}),
          },
        })
        .then((res) => {
          const edges = Array.isArray(res.data) ? res.data : res.data?.edges || [];
          resultEdges = edges;
          setMinutesMap((prev) => {
            const next = new Map(prev);
            edges.forEach((edge) => {
              const k = makeKey(
                edge.schoolId || schoolId,
                edge.todBucket || bucket,
                edge.origin?.key || edge.origin || edge.fromKey,
                edge.destination?.key || edge.destination || edge.toKey
              );
              const m = Number(edge.minutes);
              if (!isNaN(m)) next.set(k, m);
            });
            return next;
          });
        })
        .catch(() => {
          resultEdges = [];
        });

      pairs.forEach(({ fromKey, toKey }) => {
        const key = makeKey(schoolId, bucket, fromKey, toKey);
        pending.current.set(key, req);
      });
      await req;
      pairs.forEach(({ fromKey, toKey }) => {
        const key = makeKey(schoolId, bucket, fromKey, toKey);
        pending.current.delete(key);
      });
      return resultEdges;
    },
    [bucket, minutesMap, schoolId]
  );

  const seedEdges = useCallback(
    (edges) => {
      if (!Array.isArray(edges) || !edges.length) return;
      setMinutesMap((prev) => {
        const next = new Map(prev);
        edges.forEach((edge) => {
          const originKey = edge.origin?.key || edge.origin || edge.fromKey || edge.from;
          const destKey = edge.destination?.key || edge.destination || edge.toKey || edge.to;
          const school = edge.schoolId || schoolId;
          const tod = edge.todBucket || edge.bucket || bucket;
          if (!originKey || !destKey || !school || !tod) return;
          const m = Number(edge.minutes);
          if (!Number.isFinite(m)) return;
          const forward = makeKey(school, tod, originKey, destKey);
          const reverse = makeKey(school, tod, destKey, originKey);
          next.set(forward, m);
          next.set(reverse, m);
        });
        return next;
      });
    },
    [bucket, schoolId]
  );

  const api = useMemo(
    () => ({
      minutesBetween,
      minutesMap,
      ensureEdge,
      ensureAllPairs,
      seedEdges,
    }),
    [ensureEdge, ensureAllPairs, minutesBetween, minutesMap, seedEdges]
  );

  return api;
}
